import type { AuditEvent, AuditQuery, AuditResponse } from "../../shared/audit-schemas";

export const AUDIT_EXPORT_LIMIT = 1000;
function cell(value: string | number | null | undefined) {
  const text = String(value ?? "");
  // Spreadsheet formulas can start after whitespace or control characters.
  const safe =
    /^\s*[=+@-]/.test(text.replaceAll(/\p{Cc}/gu, "")) || /^[\t\r\n]/.test(text)
      ? `'${text}`
      : text;
  return `"${safe.replaceAll('"', '""')}"`;
}
export function auditCsv(events: AuditEvent[]): string {
  const header = [
    "Time (UTC)",
    "Person",
    "Person ID",
    "Application",
    "Application ID",
    "Customer",
    "Record",
    "Action",
    "Outcome",
    "Event",
    "Request ID",
    "Event ID",
  ];
  const rows = events.map((event) =>
    [
      new Date(event.createdAt * 1000).toISOString(),
      event.actorDisplayName,
      event.actorUserId,
      event.appName,
      event.appInstanceId,
      event.customerName,
      event.subjectKey,
      event.payload["action"],
      event.outcome,
      event.event,
      event.requestId,
      event.id,
    ]
      .map((value) => cell(typeof value === "boolean" ? String(value) : value))
      .join(","),
  );
  return "\uFEFF" + [header.map(cell).join(","), ...rows].join("\r\n") + "\r\n";
}

/** Each page reuses the authenticated endpoint and frozen filters; failures never yield a partial CSV. */
export async function collectAuditExport(input: {
  query: AuditQuery;
  read: (query: AuditQuery, signal: AbortSignal) => Promise<AuditResponse>;
  signal: AbortSignal;
  now: number;
  progress: (count: number) => void;
}) {
  const query = {
    ...input.query,
    cursor: undefined,
    pageSize: 50 as const,
    to: Math.min(input.query.to ?? input.now, input.now),
  };
  const events: AuditEvent[] = [];
  const cursors = new Set<string>();
  let cursor: string | undefined;
  do {
    input.signal.throwIfAborted();
    const page = await input.read({ ...query, cursor }, input.signal);
    input.signal.throwIfAborted();
    events.push(...page.events);
    input.progress(events.length);
    if (page.nextCursor && events.length >= AUDIT_EXPORT_LIMIT)
      throw new Error(
        "More than 1,000 events match. Narrow the date range or filters and export again.",
      );
    if (page.nextCursor && cursors.has(page.nextCursor))
      throw new Error("Export pagination could not continue. Try again.");
    if (page.nextCursor && cursors.size >= 19)
      throw new Error("Export could not finish within 20 pages. Narrow the filters and try again.");
    cursor = page.nextCursor ?? undefined;
    if (cursor) cursors.add(cursor);
  } while (cursor);
  return auditCsv(events);
}
